// Review Center — three owner-side review surfaces in one page:
//
//   Proposals       companyAdmin / issueApproveResolution users review
//                   pending IssueProposals (approve = credit proposer +
//                   resolve; reject = send back).
//   Triage          partner-created issues awaiting owner confirmation
//                   (PUT /issue/:id/triage — accept as-is or fix
//                   priority/due/location first).
//   Checklist       checklist items a link user marked complete, now
//   completions     pending_review — approve (→ complete) or reject
//                   with a reason (→ back to in_progress).
//
// Data:
//   GET  /issue/proposals/queue?projectId=
//   POST /issue/proposals/:id/approve | /reject   { note? }
//   GET  /issue?projectId=&needsTriage=true       (+ gantt feed merge)
//   PUT  /issue/:id/triage
//   GET  /checklist-reviews/queue?projectId=
//   POST /checklist-reviews/:id/approve | /reject { note? }  (reject: note required)
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Card, Empty, Button, Select, Tag, Skeleton, message, Input, Avatar, Tooltip, Tabs, Modal, Table, Badge, Space,
} from "antd";
import { CheckOutlined, CloseOutlined, EditOutlined, EyeOutlined, BranchesOutlined, ReloadOutlined } from "@ant-design/icons";
import { url, tokenRef } from "api";
import MinutesTab from "./MinutesTab";
import MarkupsTab from "./MarkupsTab";
import {
  getIssues, triageIssue, getTriageQueue,
} from "api/issue/issueApi";
import { getGanttFeed } from "api/gantt/ganttApi";
import PageContainer from "components/PageContainer/PageContainer";
import CoordinationBreadcrumbs from "components/Coordination/CoordinationBreadcrumbs";
import ModelEditsDrawer from "components/Coordination/ModelEditsDrawer";
import { matchesReviewFilter, todayIso } from "helpers/reviewFilter";
import DateRangeFilter from "components/Coordination/DateRangeFilter";
import { rangeEmpty } from "helpers/dateRange";
import useLastProject from "components/Coordination/useLastProject";
import { T } from "pages/Issue/theme";
import { EvidenceList } from "components/Issue/IssueSourceControls";
import { proposalEvidenceItems } from "helpers/proposalEvidence";
import { issueLabel, labelFor } from "helpers/artifactPrefix";
import { useBulkSelection, BulkBar, RowCheck, runBatch } from "components/Coordination/BulkSelect";
import { getRevisionQueue, approveRevision, rejectRevision } from "api/commits/commitsApi";
import { pruneFinishedIssues, useIssueListState } from "helpers/issueListPrune";
import { useDispatch } from "react-redux";
import { issueFinished, refreshIssueLists, rowSettled, rowsSettled } from "actions/issues";
import { useSettledRows, SettledBadge, settledStyle } from "helpers/useSettledRows";
import DocumentationReview from "components/Coordination/Glance/review/DocumentationReview";
import ShopDrawingsTab from "./ShopDrawingsTab";
import PartnerImportTab from "./PartnerImportTab";
import ApprovalTab from "./ApprovalTab";
import useProjectAccess from "hooks/useProjectAccess";
import { usePreference } from "helpers/userPrefs";
import { activityLabel, activityTitle } from "helpers/schedule/activityLabel";
import { matchOption } from "components/SearchSelect/searchSelect";


// ─── Revisions ────────────────────────────────────────────────────────────
//
// Model pushes waiting to be merged, on projects with
// requireCommitApproval on. Until a reviewer decides, the push is stored
// and inspectable but is NOT part of the project's history: it is not
// the baseline the next push diffs against and it writes no element
// history. Approving merges it; sending it back is terminal.
//
// GET  /commits/review-queue
// POST /commits/:id/approve | /reject   { note? }
function RevisionsTab({ projectId, filter, onCountChange, reloadKey }) {
  const history = useHistory();
  const dispatch = useDispatch();
  const settled = useSettledRows("revisions");
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState("");
  const [note, setNote] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await getRevisionQueue({ projectId });
      const list = (r?.data?.rows || r?.rows || []);
      setRows(list);
      if (onCountChange) onCountChange(list.length);
    } catch (e) {
      setRows([]);
      if (onCountChange) onCountChange(0);
    }
  // reloadKey: the header refresh icon (see the page component below).
  }, [projectId, onCountChange, reloadKey]);

  useEffect(() => { load(); }, [load]);

  const decide = async (row, action) => {
    setBusy(row.commitId + action);
    try {
      const fn = action === "approve" ? approveRevision : rejectRevision;
      await fn(row.commitId, { note });
      // Answer on the row itself first; the refetch below only confirms.
      dispatch(rowSettled("revisions", row.commitId, action === "approve" ? "Approved" : "Sent back"));
      message.success(action === "approve"
        ? "Revision approved — it is now part of the project history"
        : "Revision sent back — it stays out of the project history");
      setNote("");
      await load();
    } catch (e) {
      // api/index.js throws the parsed error BODY, not an Error.
      message.error(e?.err || e?.message || `Could not ${action} the revision`);
    } finally {
      setBusy("");
    }
  };

  const visible = settled.apply((rows || []).filter((r) => matchesReviewFilter(r, filter)), (r) => r.commitId);
  const sel = useBulkSelection(visible.filter((r) => !r._settled).map((r) => r.commitId));

  if (rows === null) return <Skeleton active paragraph={{ rows: 4 }} />;
  if (visible.length === 0) {
    // An empty queue has two very different causes, and saying only
    // "nothing waiting" hides the common one: the gate is off, so every
    // push merged on arrival and nothing was ever meant to queue here.
    return (
      <Empty
        description={(
          <span>
            No model revisions waiting for approval
            <div style={{ fontSize: 12, color: "#8c8c8c", marginTop: 6 }}>
              Pushes queue here only on projects with <b>Require approval for model pushes</b>
              {" "}switched on in the project's settings. With it off, every push merges as it lands.
            </div>
          </span>
        )}
      />
    );
  }

  const byId = new Map(visible.map((r) => [r.commitId, r]));
  const bulk = async (ids, action) => {
    const fn = action === "approve" ? approveRevision : rejectRevision;
    const { failed } = await runBatch(ids, (id) => fn(id, { note }), {
      verb: action === "approve" ? "merged" : "sent back",
    });
    // Only the ones the server actually took: a failed id stays live.
    const bad = new Set(failed.map((f) => String(f.id)));
    dispatch(rowsSettled("revisions", ids.filter((id) => !bad.has(String(id))), action === "approve" ? "Approved" : "Sent back"));
    setNote("");
    await load();
  };

  return (
    <div>
      <BulkBar
        sel={sel}
        busy={busy}
        hint="the note below applies to every checked revision"
        actions={[
          { key: "approve", label: "Approve & merge", icon: <CheckOutlined />, primary: true, run: (ids) => bulk(ids, "approve") },
          { key: "reject", label: "Send back", icon: <CloseOutlined />, danger: true, run: (ids) => bulk(ids, "reject") },
        ]}
      />
      {visible.map((r) => {
        const t = r.totals || {};
        const who = r.user
          ? `${[r.user.name, r.user.lastName].filter(Boolean).join(" ") || r.user.email}`
          : "Unknown";
        return (
          <Card key={r.commitId} size="small" style={{ marginBottom: 10, borderColor: "#F5B14C", background: "#FFFBF3", ...settledStyle(r) }}>
            <div style={{ display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
              <RowCheck sel={sel} id={r.commitId} style={{ marginTop: 2 }} />
              <SettledBadge row={r} style={{ marginLeft: 0 }} />
              <div style={{ flex: 1, minWidth: 260 }}>
                <div style={{ fontWeight: 600, color: T.ink }}>
                  {r.label || r.revitDocTitle || "Model push"}
                  {r.scope === "selection" && (
                    <Tag style={{ ...NAVY_TAG, marginLeft: 8 }}>selection</Tag>
                  )}
                </div>
                <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>
                  {r.projectTitle} · pushed by {who} · {new Date(r.ts).toLocaleString()}
                  {r.revitVersion ? ` · Revit ${r.revitVersion}` : ""}
                  {r.pluginVersion ? ` · plugin ${r.pluginVersion}` : ""}
                </div>
                <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <Tag style={CHANGE_TAG_STYLES.added}>{t.added || 0} added</Tag>
                  <Tag style={CHANGE_TAG_STYLES.modified}>{t.modified || 0} modified</Tag>
                  <Tag style={CHANGE_TAG_STYLES.removed}>{t.removed || 0} removed</Tag>
                  <Tag style={NEUTRAL_TAG}>{t.unchanged || 0} unchanged</Tag>
                  <Tag style={NEUTRAL_TAG}>{r.changedTiles} tile{r.changedTiles === 1 ? "" : "s"}</Tag>
                </div>
                {(r.forIssueIds || []).length > 0 && (
                  <div style={{ fontSize: 12, color: T.muted, marginTop: 6 }}>
                    Raised against {r.forIssueIds.length} issue{r.forIssueIds.length === 1 ? "" : "s"}
                  </div>
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 200 }}>
                <Button
                  icon={<BranchesOutlined />}
                  onClick={() => history.push(`/personal-area/projects/model-view/${r.projectId}?diff=${r.commitId}`)}
                  title="Open the streamed model with this revision's change painted on it"
                >
                  Review in 3D
                </Button>
                <Input.TextArea
                  value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder="Note (optional)" autoSize={{ minRows: 1, maxRows: 3 }}
                />
                <Space>
                  <Button type="primary" style={navyBtn} icon={<CheckOutlined />}
                          loading={busy === r.commitId + "approve"}
                          onClick={() => decide(r, "approve")}>
                    Approve & merge
                  </Button>
                  <Button danger icon={<CloseOutlined />}
                          loading={busy === r.commitId + "reject"}
                          onClick={() => decide(r, "reject")}>
                    Send back
                  </Button>
                </Space>
              </div>
            </div>
          </Card>
        );
      })}
    </div>
  );
}

// BidLight chip styles (Tag style props) — replaces the antd preset colors.
const navyBtn = { background: T.navy, borderColor: T.navy };
const AMBER_TAG = { background: T.ambBg, borderColor: "#F1DFB1", color: "#8A6112" };
const NAVY_TAG = { background: T.wash, borderColor: "#D4E3EC", color: T.navyD };
const NEUTRAL_TAG = { background: "#F3F4F5", borderColor: "#E8EAEC", color: "#526470" };
const PRIORITY_TAG_STYLES = {
  urgent: { background: T.removedBg, borderColor: "#FAD6D6", color: T.removed2 },
  normal: NAVY_TAG,
  low: NEUTRAL_TAG,
};
const CHANGE_TAG_STYLES = {
  added: { background: T.addedBg, borderColor: "#CDEEC1", color: T.added2 },
  removed: { background: T.removedBg, borderColor: "#FAD6D6", color: T.removed2 },
  modified: AMBER_TAG,
  unchanged: NEUTRAL_TAG,
  not_in_model: { background: "#F1E8FB", borderColor: "#E3D3F5", color: "#7A4BBF" },
};

const dateInputStyle = {
  border: `1px solid ${T.line2}`,
  borderRadius: 6,
  padding: "3px 8px",
  fontSize: 13,
  width: "100%",
};

// Every review tab offers a way to LOOK at the thing before deciding:
// issues open on their resolve page (new tab), so the reviewer keeps
// the queue.
function issueHref(issue) {
  if (!issue) return null;
  return issue.shareToken ? `/issue/${issue.shareToken}/resolve` : `/issue-viewer/${issue._id || issue}`;
}
function ViewIssueButton({ issue, size = "small", label = "View issue" }) {
  const href = issueHref(issue);
  if (!href) return null;
  return (
    <Tooltip title="Open the issue (new tab) - image, markup, attachments, activity">
      <Button size={size} icon={<EyeOutlined />} href={href} target="_blank" rel="noopener noreferrer">
        {label}
      </Button>
    </Tooltip>
  );
}
const NO_DELTA_HINT = "No model diff yet - a delta appears once a resolution with a commit is pushed from Revit or Navisworks";

function auth() {
  return { Authorization: `Bearer ${localStorage.getItem(tokenRef)}` };
}

async function fetchQueue(projectId) {
  const qs = projectId ? `?projectId=${projectId}` : "";
  const res = await fetch(`${url}/issue/proposals/queue${qs}`, { headers: auth() });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}
async function approve(id, note) {
  const res = await fetch(`${url}/issue/proposals/${id}/approve`, {
    method: "POST",
    headers: { ...auth(), "Content-Type": "application/json" },
    body: JSON.stringify({ note }),
  });
  if (!res.ok) throw new Error((await res.json())?.err || `HTTP ${res.status}`);
  return res.json();
}
async function reject(id, note) {
  const res = await fetch(`${url}/issue/proposals/${id}/reject`, {
    method: "POST",
    headers: { ...auth(), "Content-Type": "application/json" },
    body: JSON.stringify({ note }),
  });
  if (!res.ok) throw new Error((await res.json())?.err || `HTTP ${res.status}`);
  return res.json();
}
// Claims: issues waiting on a decision that no submission covers - a
// link partner's resolution (no account, so no proposal was ever filed)
// or an issue somebody parked in review by hand. They used to be
// invisible here, which left them stuck at "In review" with nothing to
// approve and nothing to send back.
async function fetchClaims(projectId) {
  const qs = projectId ? `?projectId=${projectId}` : "";
  const res = await fetch(`${url}/issue/claims/queue${qs}`, { headers: auth() });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}
async function decideClaim(issueId, action, note) {
  const res = await fetch(`${url}/issue/claims/${issueId}/${action}`, {
    method: "POST",
    headers: { ...auth(), "Content-Type": "application/json" },
    body: JSON.stringify({ note }),
  });
  if (!res.ok) throw new Error((await res.json())?.err || `HTTP ${res.status}`);
  return res.json();
}
async function fetchChecklistQueue(projectId) {
  const qs = projectId ? `?projectId=${projectId}` : "";
  const res = await fetch(`${url}/checklist-reviews/queue${qs}`, { headers: auth() });
  if (!res.ok) {
    const err = new Error(`HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}
async function reviewChecklist(id, action, note) {
  const res = await fetch(`${url}/checklist-reviews/${id}/${action}`, {
    method: "POST",
    headers: { ...auth(), "Content-Type": "application/json" },
    body: JSON.stringify({ note: note || undefined }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body?.err || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}
// The tab badges: what is pending on every tab of one project and how
// much of it this person has not looked at yet, in one call - so a tab
// carries its number before it is ever opened. Opening a tab reports
// what was on it as seen.
async function fetchBadges(projectId) {
  const res = await fetch(`${url}/review-center/badges?projectId=${encodeURIComponent(projectId)}`, { headers: auth() });
  if (!res.ok) throw new Error("Could not read the review counts");
  return res.json();
}
async function markTabSeen(projectId, tab, ids) {
  const res = await fetch(`${url}/review-center/seen`, {
    method: "POST", headers: { "Content-Type": "application/json", ...auth() },
    body: JSON.stringify({ projectId, tab, ids }),
  });
  if (!res.ok) throw new Error("Could not record what was seen");
  return res.json();
}

// `include`: the project the page was opened on, so the picker names it
// even when the reader is not on its roster (it showed the raw id).
async function fetchProjects(include = "") {
  const res = await fetch(`${url}/projects/picker${include ? `?include=${encodeURIComponent(include)}` : ""}`, { headers: auth() });
  if (!res.ok) return [];
  const body = await res.json();
  return Array.isArray(body) ? body : (body?.projects || []);
}

// ============================ Proposals tab ================================

// WHAT THE PROPOSAL IS ASKING FOR. Every proposal used to mean the same
// thing - "I resolved this" - because resolution was the only ending
// anybody outside the job could claim. A partner asking to CLOSE or to
// ABANDON an issue files a proposal too (back/utils/issueReviewGate),
// and Approve applies the ending that was requested, so the card has to
// say which one it is before anyone presses it. Older proposals carry
// no requestedStatus and are resolutions.
const REQUEST_WORDS = {
  resolved: { tag: "resolution", approve: "Approve resolution" },
  abandoned: { tag: "abandon", approve: "Approve abandon" },
};
const requestWords = (p) => REQUEST_WORDS[p?.requestedStatus] || REQUEST_WORDS.resolved;
// Abandoning is called-off work, not finished work - the one request in
// the queue that ends an issue with nothing delivered.
const ABANDON_TAG = { background: "#FBEAEA", borderColor: "#F0C9C9", color: "#9B2C2C" };

// Hours that came with the request - on every card for owner-company
// reviewers, admins and members (back/utils/issueHours canSeeHoursSpent).
// The cost is sent only to the project owner and company admins, so it
// shows only when the server included it.
function HoursLine({ h }) {
  if (!h) return null;
  const hours = h.hours != null
    ? `${Number(h.hours).toLocaleString("en-US", { maximumFractionDigits: 2 })} h`
    : h.name ? "not entered (desktop plugin)" : "not entered";
  const cost = !("cost" in h) || h.hours == null ? ""
    : h.cost == null ? " · no rate set"
      : ` · $${Number(h.cost).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  return (
    <div style={{ fontSize: 12.5, color: T.text, marginBottom: 8 }}>
      <b>Hours spent:</b> {hours}{cost}{h.companyName ? <span style={{ color: T.muted }}> · {h.companyName}</span> : null}
    </div>
  );
}

function ProposalCard({ p, onApprove, onReject, working, sel }) {
  const u = p.proposerId || {};
  const fullName = `${u.name || ""} ${u.lastName || ""}`.trim() || "Contributor";
  const [note, setNote] = useState("");
  const [editsOpen, setEditsOpen] = useState(false);
  const editsCommitId = p.commitId && (p.commitId._id || p.commitId);
  const submitted = new Date(p.submittedAt).toLocaleString();
  const w = requestWords(p);
  const isAbandon = p?.requestedStatus === "abandoned";
  return (
    <Card size="small" style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
        {sel && <RowCheck sel={sel} id={String(p._id)} />}
        <Avatar style={{ background: T.navy, marginRight: 8 }}>
          {fullName.charAt(0).toUpperCase()}
        </Avatar>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600 }}>{fullName}</div>
          <div style={{ fontSize: 12, color: T.muted }}>{submitted}</div>
        </div>
        <Tag style={isAbandon ? ABANDON_TAG : AMBER_TAG}>
          {w.tag} · pending
        </Tag>
      </div>
      {p.notes && (
        <div style={{ fontSize: 13, color: T.text, marginBottom: 8, fontStyle: "italic" }}>
          "{p.notes}"
        </div>
      )}
      <HoursLine h={p.hoursSpent} />
      {/* Resolution screenshots / documents (also filed in Document Control).
          Union of evidence[] + screenshotUrls[] + the per-element shots the
          Revit / Navisworks plugin sends - see helpers/proposalEvidence. */}
      <div style={{ marginBottom: 8 }}>
        <EvidenceList compact title="EVIDENCE" items={proposalEvidenceItems(p)} issue={p.issueId} />
      </div>
      {/* Elements only exist on a plugin submission. A request to close
          or abandon an issue moves nothing, and "0 element(s)" read as
          a submission that had failed to include any. */}
      {(p.resolvedElements || []).length > 0 && (
        <div style={{ fontSize: 12, color: T.muted, marginBottom: 8 }}>
          {(p.resolvedElements || []).length} element(s):
          <div style={{ marginTop: 4, fontFamily: "monospace", fontSize: 11 }}>
            {(p.resolvedElements || []).slice(0, 5).map(e => (
              <div key={e.uniqueId}>
                · {e.uniqueId}{e.fragmentHash ? ` · ${e.fragmentHash.slice(0, 8)}…` : ""}
                {e.tileKey ? ` · tile ${e.tileKey}` : ""}
              </div>
            ))}
            {p.resolvedElements?.length > 5 && (
              <div style={{ color: T.muted }}>+{p.resolvedElements.length - 5} more</div>
            )}
          </div>
        </div>
      )}
      {editsCommitId && (
        <>
          <Button size="small" block style={{ marginBottom: 8 }} onClick={() => setEditsOpen(true)}
                  title="Old and new geometry of the changed elements in one scene, with a slider between the two generations">
            Show model edits (old / new)
          </Button>
          <ModelEditsDrawer
            open={editsOpen}
            commitId={String(editsCommitId)}
            projectTitle={p.projectTitle || ""}
            onClose={() => setEditsOpen(false)}
          />
        </>
      )}
      {isAbandon && (
        <div style={{ fontSize: 12, color: "#9B2C2C", marginBottom: 8 }}>
          Approving calls this work off - the issue ends unresolved and
          leaves its activity's progress.
        </div>
      )}
      <Input.TextArea
        rows={2} placeholder="Review note (optional)"
        value={note} onChange={e => setNote(e.target.value)}
        style={{ marginBottom: 8, fontSize: 12 }}
      />
      <div style={{ display: "flex", gap: 8 }}>
        <Button type="primary" style={navyBtn} icon={<CheckOutlined />} loading={working}
                onClick={() => onApprove(p._id, note)} block>
          {w.approve}
        </Button>
        <Tooltip title="Send back to the contributor">
          <Button danger icon={<CloseOutlined />} loading={working}
                  onClick={() => onReject(p._id, note)} block>
            Reject
          </Button>
        </Tooltip>
      </div>
    </Card>
  );
}

// One claim. It carries no elements and usually no commit - what the
// reviewer is agreeing with is the claimant, their note and whatever
// they attached to the issue itself.
function ClaimCard({ c, onDecide, working, settledLabel }) {
  const [note, setNote] = useState("");
  const who = c.claimedBy?.name || "Someone";
  const when = c.claimedAt ? new Date(c.claimedAt).toLocaleString() : "";
  const evidence = (c.evidence || []).map((e) => ({
    url: e.url, name: e.name || "", mime: e.mime || "", kind: e.kind || "image",
  }));
  return (
    <Card style={{ marginBottom: 16, background: T.bg, borderColor: T.line, ...settledStyle({ _settled: settledLabel }) }}
          title={
            <span>
              {c.issue?.displayId ? `${issueLabel(c.issue)} — ` : ""}{c.issue?.title || "Unknown issue"}
              <SettledBadge row={{ _settled: settledLabel }} />
            </span>
          }
          extra={
            <span style={{ fontSize: 12, color: T.muted, display: "inline-flex", alignItems: "center", gap: 8 }}>
              <Tag style={AMBER_TAG}>waiting · no submission</Tag>
              <ViewIssueButton issue={c.issue} />
            </span>
          }>
      <div style={{ fontSize: 13, color: T.text, marginBottom: 6 }}>
        <b>{who}</b>
        {c.claimedBy?.kind === "guest" ? " (on a shared link)" : ""}
        {" "}said this is finished{when ? ` · ${when}` : ""}.
      </div>
      {c.notes && (
        <div style={{ fontSize: 13, color: T.text, marginBottom: 8, fontStyle: "italic" }}>
          "{c.notes}"
        </div>
      )}
      <HoursLine h={c.hoursSpent} />
      {evidence.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <EvidenceList compact title="EVIDENCE" items={evidence} issue={c.issue} />
        </div>
      )}
      <Input.TextArea
        rows={2} placeholder="Review note (optional)"
        value={note} onChange={(e) => setNote(e.target.value)}
        style={{ marginBottom: 8, fontSize: 12 }}
      />
      <div style={{ display: "flex", gap: 8 }}>
        <Button type="primary" style={navyBtn} icon={<CheckOutlined />} loading={working}
                onClick={() => onDecide(c.issue._id, "approve", note)} block>
          Approve · resolve it
        </Button>
        <Tooltip title="Back to open, with your note on the issue">
          <Button danger icon={<CloseOutlined />} loading={working}
                  onClick={() => onDecide(c.issue._id, "reject", note)} block>
            Send back
          </Button>
        </Tooltip>
      </div>
    </Card>
  );
}

function IssueGroup({ issue, proposals, onApprove, onReject, working, sel, settledLabel }) {
  const isCompare = proposals.length >= 2;
  return (
    <Card style={{ marginBottom: 16, background: T.bg, borderColor: T.line, ...settledStyle({ _settled: settledLabel }) }}
          title={
            <span>
              {issue?.displayId ? `${issueLabel(issue)} — ` : ""}{issue?.title || "Unknown issue"}
              <SettledBadge row={{ _settled: settledLabel }} />
            </span>
          }
          extra={
            <span style={{ fontSize: 12, color: T.muted, display: "inline-flex", alignItems: "center", gap: 8 }}>
              <span>
                {proposals.length} {isCompare ? "competing proposals" : "proposal"}
                {issue?.priority && (
                  <> · <Tag style={PRIORITY_TAG_STYLES[issue.priority] || NAVY_TAG}>{issue.priority}</Tag></>
                )}
              </span>
              <ViewIssueButton issue={issue} />
            </span>
          }>
      {isCompare ? (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {proposals.map(p => (
            <ProposalCard key={p._id} p={p} sel={sel} sel={sel}
                          onApprove={onApprove} onReject={onReject} working={working} />
          ))}
        </div>
      ) : (
        proposals.map(p => (
          <ProposalCard key={p._id} p={p}
                        onApprove={onApprove} onReject={onReject} working={working} />
        ))
      )}
    </Card>
  );
}

function ProposalsTab({ projectId, filter, reloadKey }) {
  const dispatch = useDispatch();
  const { finished, reopened } = useIssueListState();
  const settled = useSettledRows("proposals");
  const [queue, setQueue] = useState([]);
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);

  // Only the FIRST load shows a skeleton. A refetch after a decision keeps
  // the rows on screen - including the one just badged - until the new
  // set arrives; blanking the list here is what used to eat the badge.
  const loadedOnce = useRef(false);
  const load = useCallback(() => {
    if (!loadedOnce.current) setLoading(true);
    Promise.all([
      fetchQueue(projectId),
      // A claim queue that fails must not blank the proposals: they are
      // two questions about the same tab, not one request.
      fetchClaims(projectId).catch(() => []),
    ])
      .then(([q, c]) => { setQueue(q); setClaims(Array.isArray(c) ? c : []); loadedOnce.current = true; })
      .catch(e => message.error(e.message))
      .finally(() => setLoading(false));
  }, [projectId, reloadKey]);

  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const byIssue = new Map();
    for (const p of queue) {
      const iid = String(p.issueId?._id || p.issueId);
      if (!byIssue.has(iid)) byIssue.set(iid, { issue: p.issueId, proposals: [] });
      byIssue.get(iid).proposals.push(p);
    }
    return Array.from(byIssue.values());
  }, [queue]);

  const handleApprove = async (id, note) => {
    setWorking(true);
    try {
      await approve(id, note);
      // Approving a resolution resolves the ISSUE, so it leaves the issue
      // lists too - not just this queue. `id` is the proposal's.
      const p = queue.find((x) => String(x._id) === String(id));
      if (p) {
        dispatch(issueFinished(String(p.issueId?._id || p.issueId), "resolved"));
        dispatch(rowSettled("proposals", String(p.issueId?._id || p.issueId), "Approved"));
      }
      load();
      message.success("Approved");
    }
    catch (e) { message.error(e.message); }
    finally { setWorking(false); }
  };
  const handleReject = async (id, note) => {
    setWorking(true);
    try {
      await reject(id, note);
      const p = queue.find((x) => String(x._id) === String(id));
      if (p) dispatch(rowSettled("proposals", String(p.issueId?._id || p.issueId), "Sent back"));
      load(); message.success("Rejected");
    }
    catch (e) { message.error(e.message); }
    finally { setWorking(false); }
  };
  const handleClaim = async (issueId, action, note) => {
    setWorking(true);
    try {
      await decideClaim(issueId, action, note);
      if (action === "approve") dispatch(issueFinished(String(issueId), "resolved"));
      dispatch(rowSettled("proposals", String(issueId), action === "approve" ? "Resolved" : "Sent back"));
      load();
      message.success(action === "approve" ? "Resolved" : "Sent back to open");
    } catch (e) { message.error(e.message); }
    finally { setWorking(false); }
  };

  // A card whose issue has just been finished - here, or on the issue's own
  // page in another tab of the same session - has nothing left to review.
  const issueIdOf = (r) => r?.issue?._id || r?.issueId?._id || r?.issueId || r?._id;
  // A group / claim just decided stays for one beat, badged, then goes.
  // The redux prune below it would drop it in the same frame - so the
  // settle runs FIRST and a badged row is exempt from the prune.
  const settleFirst = (list) => settled.apply(list, issueIdOf);
  const exempt = (list) => list.filter((r) => r._settled);
  const vg = settleFirst(grouped.filter((g) => matchesReviewFilter(g, filter)));
  const vc = settleFirst(claims.filter((c) => matchesReviewFilter(c, filter)));
  const visibleGroups = [...exempt(vg), ...pruneFinishedIssues(vg.filter((g) => !g._settled), finished, reopened, { idOf: issueIdOf })];
  const visibleClaims = [...exempt(vc), ...pruneFinishedIssues(vc.filter((c) => !c._settled), finished, reopened, { idOf: issueIdOf })];
  const sel = useBulkSelection(
    visibleGroups.filter((g) => !g._settled).flatMap((g) => (g.proposals || []).map((p) => String(p._id)))
  );
  const [bulkNote, setBulkNote] = useState("");
  // A decision taken in bulk still owes the requester its reason. The
  // per-card note never reached this path - every batched approval and
  // refusal went out blank, so the comment on the issue read "Approved
  // the issue." and the requester's email had nothing under the title.
  // One note, applied to everything checked, the way the revisions tab
  // already does it.
  const bulk = async (ids, action) => {
    await runBatch(ids, (id) => (action === "approve" ? approve(id, bulkNote.trim()) : reject(id, bulkNote.trim())), {
      verb: action === "approve" ? "approved" : "sent back",
    });
    const ids0 = new Set(ids.map(String));
    for (const p of queue) {
      if (!ids0.has(String(p._id))) continue;
      const iid = String(p.issueId?._id || p.issueId);
      if (action === "approve") dispatch(issueFinished(iid, "resolved"));
      dispatch(rowSettled("proposals", iid, action === "approve" ? "Approved" : "Sent back"));
    }
    setBulkNote("");
    load();
  };

  return (
    <>
      {loading && <Skeleton active paragraph={{ rows: 4 }} />}

      {!loading && (
        <>
          <BulkBar
            sel={sel}
            busy={working}
            hint="the note below applies to every checked request"
            actions={[
              { key: "approve", label: "Approve selected", icon: <CheckOutlined />, primary: true, run: (ids) => bulk(ids, "approve") },
              { key: "reject", label: "Send back selected", icon: <CloseOutlined />, danger: true, run: (ids) => bulk(ids, "reject") },
            ]}
          />
          {sel.count > 0 && (
            <Input.TextArea
              value={bulkNote} onChange={(e) => setBulkNote(e.target.value)}
              placeholder="Why - sent to whoever asked, and posted on each issue (optional)"
              autoSize={{ minRows: 1, maxRows: 3 }}
              style={{ marginBottom: 10 }}
            />
          )}
        </>
      )}

      {!loading && grouped.length > 0 && visibleGroups.length === 0 && visibleClaims.length === 0 && (
        <Empty description="No proposals match the filter" />
      )}
      {!loading && grouped.length === 0 && visibleClaims.length === 0 && (
        <Empty description={
          <span>
            No pending proposals. Either no one has submitted any, or the
            owning projects have <code>requireProposalApproval</code> off.
          </span>
        } />
      )}

      {!loading && visibleClaims.length > 0 && (
        <>
          <div style={{ fontSize: 12, color: T.muted, margin: "4px 0 8px" }}>
            <b style={{ color: T.text }}>Waiting on you, with nothing submitted</b> — someone
            said these are finished without filing a resolution: a partner working from a
            shared link (no account, so there is no submission to attach), or an issue moved
            into review by hand. They stay here until you answer.
          </div>
          {visibleClaims.map((c) => (
            <ClaimCard key={String(c.issue._id)} c={c} onDecide={handleClaim} working={working} settledLabel={c._settled} />
          ))}
        </>
      )}

      {!loading && visibleGroups.map(g => (
        <IssueGroup key={g.issue?._id || JSON.stringify(g.issue)}
                    issue={g.issue} proposals={g.proposals}
                    onApprove={handleApprove} onReject={handleReject}
                    working={working} sel={sel} settledLabel={g._settled} />
      ))}
    </>
  );
}

// ============================= Triage tab ==================================
//
// The list endpoint's projection is slim (no displayId/priority/
// location), so rows are joined with the gantt feed's richer issue
// slims by _id.

function TriageTab({ projectId, filter, reloadKey }) {
  const history = useHistory();
  const dispatch = useDispatch();
  const { finished, reopened } = useIssueListState();
  const settled = useSettledRows("triage");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const [editRow, setEditRow] = useState(null); // issue being edit-&-accepted
  const [editForm, setEditForm] = useState({});

  const loadedOnce = useRef(false);
  const load = useCallback(() => {
    if (!projectId) { setRows([]); return; }
    if (!loadedOnce.current) setLoading(true);
    Promise.all([getTriageQueue(projectId), getGanttFeed(projectId)])
      .then(([list, feed]) => {
        const feedById = new Map(
          (feed?.issues || []).map((i) => [String(i._id), i])
        );
        setRows(
          (list?.issues || []).map((r) => ({
            ...(feedById.get(String(r._id)) || {}),
            ...r,
          }))
        );
      })
      .catch((e) => message.error(e.message || "Failed to load triage queue"))
      .finally(() => { setLoading(false); loadedOnce.current = true; });
  }, [projectId, reloadKey]);

  useEffect(() => { load(); }, [load]);

  const accept = async (row, body) => {
    setWorking(true);
    try {
      await triageIssue(row._id, body || {});
      dispatch(rowSettled("triage", row._id, "Accepted"));
      message.success(`${row.displayId ? `${issueLabel(row)}` : "Issue"} accepted`);
      setEditRow(null);
      load();
    } catch (e) {
      if (e.status === 403) {
        message.error("Only the project-owner side can triage issues");
      } else {
        message.error(e.message || "Triage failed");
      }
    } finally {
      setWorking(false);
    }
  };

  const openEdit = (row) => {
    setEditRow(row);
    setEditForm({
      priority: row.priority || "normal",
      dueDate: row.dueDate ? String(row.dueDate).slice(0, 10) : "",
      area: row.location?.area || "",
      level: row.location?.level || "",
      grid: row.location?.grid || "",
    });
  };

  const submitEdit = () => {
    const body = {
      priority: editForm.priority,
      location: {
        area: editForm.area,
        level: editForm.level,
        grid: editForm.grid,
      },
    };
    if (editForm.dueDate) body.dueDate = editForm.dueDate;
    accept(editRow, body);
  };

  // Merge-duplicate flow: pick a surviving open issue on the same
  // project; the triaged row is abandoned with everything moved over.
  const [mergeRow, setMergeRow] = useState(null);
  const [mergeTargets, setMergeTargets] = useState([]);
  const [mergeTargetId, setMergeTargetId] = useState("");
  const openMerge = async (row) => {
    setMergeRow(row);
    setMergeTargetId("");
    try {
      const res = await getIssues({ projectId, limit: 200, slim: true });
      const list = res?.issues || res;
      setMergeTargets(
        (Array.isArray(list) ? list : []).filter(
          (i) =>
            String(i._id) !== String(row._id) &&
            ["open", "pending", "under_review"].includes(i.status)
        )
      );
    } catch (e) {
      setMergeTargets([]);
    }
  };
  const submitMerge = async () => {
    if (!mergeTargetId) return message.warning("Pick the surviving issue");
    setWorking(true);
    try {
      const token = localStorage.getItem(tokenRef);
      const res = await fetch(`${url}/issue/${mergeRow._id}/merge`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ targetId: mergeTargetId }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.err || `HTTP ${res.status}`);
      message.success(
        `Merged into ${labelFor(body.target?.displayId, body.target?.artifactPrefix)} — duplicate abandoned`
      );
      setMergeRow(null);
      load();
    } catch (e) {
      message.error(e.message || "Merge failed");
    } finally {
      setWorking(false);
    }
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "displayId",
      width: 90,
      render: (v, r) => (
        <a onClick={() => history.push(`/issue-viewer/${r._id}`)}>
          {v ? labelFor(v, r.artifactPrefix) : "—"}
        </a>
      ),
    },
    { title: "Title", dataIndex: "title", render: (v, r) => <span style={settledStyle(r)}>{v}<SettledBadge row={r} /></span> },
    {
      title: "Creator",
      dataIndex: "createdBy",
      width: 160,
      render: (v) => v?.name || v?.email || "-",
    },
    {
      title: "Priority",
      dataIndex: "priority",
      width: 100,
      render: (v) =>
        v ? <Tag style={PRIORITY_TAG_STYLES[v] || NEUTRAL_TAG}>{v}</Tag> : "-",
    },
    {
      title: "Location",
      dataIndex: "location",
      width: 160,
      render: (loc) =>
        [loc?.area, loc?.level && `L${loc.level}`, loc?.grid]
          .filter(Boolean)
          .join(" · ") || "-",
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      width: 120,
      render: (v) => (v ? String(v).slice(0, 10) : "-"),
    },
    {
      title: "",
      key: "actions",
      width: 300,
      render: (_, r) => (
        <div style={{ display: "flex", gap: 8 }}>
          <ViewIssueButton issue={r} label="View" />
          <Button
            size="small"
            type="primary"
            style={navyBtn}
            icon={<CheckOutlined />}
            loading={working}
            onClick={() => accept(r)}
          >
            Accept
          </Button>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => openEdit(r)}
          >
            Edit & accept
          </Button>
          <Button size="small" danger onClick={() => openMerge(r)}>
            Merge…
          </Button>
        </div>
      ),
    },
  ];

  // Hooks before any early return - React counts them per render.
  // An issue triaged, resolved or abandoned elsewhere in this session is
  // off the queue immediately (helpers/issueListPrune.js).
  // Settle first, then prune: a row just accepted keeps its badge for a
  // beat even though redux already counts the issue as moved on.
  const settledRows = settled.apply(rows.filter((r) => matchesReviewFilter(r, filter)));
  const visible = [
    ...settledRows.filter((r) => r._settled),
    ...pruneFinishedIssues(settledRows.filter((r) => !r._settled), finished, reopened),
  ];
  const sel = useBulkSelection(visible.map((r) => String(r._id)));
  const byId = new Map(visible.map((r) => [String(r._id), r]));

  if (!projectId) {
    return <Empty description="Select a project to see its triage queue" />;
  }

  return (
    <>
      <BulkBar
        sel={sel}
        busy={working}
        hint="accepts as filed — edit individually to change priority or due date"
        actions={[
          {
            key: "accept", label: "Accept selected", icon: <CheckOutlined />, primary: true,
            // triageIssue directly, not accept(): accept() reports and
            // swallows per-row, which would fire one toast per issue and
            // hide the failures from the batch summary.
            run: async (ids) => {
              const { failed } = await runBatch(ids, (id) => triageIssue(id, {}), { verb: "accepted" });
              const bad = new Set(failed.map((f) => String(f.id)));
              dispatch(rowsSettled("triage", ids.filter((id) => !bad.has(String(id))), "Accepted"));
              load();
            },
          },
        ]}
      />
      <Table
        size="small"
        loading={loading}
        dataSource={visible}
        columns={columns}
        rowKey="_id"
        rowSelection={{
          selectedRowKeys: sel.selectedIds,
          onChange: (keys) => sel.replace(keys.map(String)),
        }}
        pagination={{ pageSize: 20, hideOnSinglePage: true }}
        locale={{ emptyText: <Empty description="No issues awaiting triage" /> }}
      />

      <Modal
        title={
          mergeRow
            ? `Merge ${mergeRow.displayId ? `${issueLabel(mergeRow)}` : "issue"} into…`
            : ""
        }
        open={!!mergeRow}
        onCancel={() => setMergeRow(null)}
        onOk={submitMerge}
        okText="Merge duplicate"
        okButtonProps={{ danger: true }}
        confirmLoading={working}
        destroyOnClose
      >
        {mergeRow && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ fontSize: 13, color: T.text }}>
              "{mergeRow.title}" will be <b>abandoned</b>; its responsible
              users, linked elements and collaborators move onto the issue
              you pick. Nothing is deleted.
            </div>
            <Select filterOption={matchOption}
              showSearch
              optionFilterProp="label"
              placeholder="Surviving issue"
              value={mergeTargetId || undefined}
              onChange={setMergeTargetId}
              options={mergeTargets.map((i) => ({
                value: i._id,
                label: `${i.displayId ? `${issueLabel(i)} — ` : ""}${i.title}`,
              }))}
            />
          </div>
        )}
      </Modal>

      <Modal
        title={
          editRow
            ? `Edit & accept ${editRow.displayId ? `${issueLabel(editRow)}` : ""}`
            : ""
        }
        open={!!editRow}
        onCancel={() => setEditRow(null)}
        onOk={submitEdit}
        okText="Accept with changes"
        confirmLoading={working}
        destroyOnClose
      >
        {editRow && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div>
              <div style={{ fontSize: 12, color: T.muted, marginBottom: 4 }}>Priority</div>
              <Select
                value={editForm.priority}
                style={{ width: "100%" }}
                options={[
                  { value: "urgent", label: "Urgent" },
                  { value: "normal", label: "Normal" },
                  { value: "low", label: "Low" },
                ]}
                onChange={(v) => setEditForm((f) => ({ ...f, priority: v }))}
              />
            </div>
            <div>
              <div style={{ fontSize: 12, color: T.muted, marginBottom: 4 }}>Due date</div>
              <input
                type="date"
                style={dateInputStyle}
                value={editForm.dueDate}
                onChange={(e) =>
                  setEditForm((f) => ({ ...f, dueDate: e.target.value }))
                }
              />
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Input
                placeholder="Area"
                value={editForm.area}
                onChange={(e) => setEditForm((f) => ({ ...f, area: e.target.value }))}
              />
              <Input
                placeholder="Level"
                value={editForm.level}
                onChange={(e) => setEditForm((f) => ({ ...f, level: e.target.value }))}
              />
              <Input
                placeholder="Grid"
                value={editForm.grid}
                onChange={(e) => setEditForm((f) => ({ ...f, grid: e.target.value }))}
              />
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}

// ======================= Checklist completions tab =========================
//
// Link users mark checklist items complete on the public validate page;
// on projects requiring approval those land here as pending_review. The
// queue lives in the page component so the tab label can badge the
// count before the tab is ever opened. No link to the validate page —
// the token isn't exposed to reviewers.

function completedByLabel(item) {
  const cb = item.completedBy || "";
  if (cb.startsWith("email:")) return cb.slice(6);
  return cb || item.currentAssignee?.name || "—";
}

function ChecklistCompletionCard({ item, onApprove, onRejectClick, working, sel }) {
  const lastComment = (item.thread || []).slice().reverse().find((t) => t.comment);
  const shot = (item.screenshots || [])[0];
  return (
    <Card size="small" style={{ marginBottom: 10, ...settledStyle(item) }} title={item._settled ? <SettledBadge row={item} style={{ marginLeft: 0 }} /> : undefined}>
      <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
        {sel && <RowCheck sel={sel} id={String(item._id)} style={{ marginTop: 2 }} />}
        {shot?.url && (
          <a href={`${url}${shot.url}`} target="_blank" rel="noreferrer">
            <img src={`${url}${shot.url}`} alt="screenshot"
                 style={{ width: 96, height: 72, objectFit: "cover", borderRadius: 6, border: `1px solid ${T.line}` }} />
          </a>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 600 }}>{item.title}</span>
            {item.ganttItemId?.name && <Tag style={NAVY_TAG} title={activityTitle(item.ganttItemId)}>{activityLabel(item.ganttItemId)}</Tag>}
            <Tag style={AMBER_TAG}>awaiting approval</Tag>
          </div>
          <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>
            {item.projectId?.title || "Unknown project"}
            {" · completed by "}<b>{completedByLabel(item)}</b>
            {item.completedAt && <> · {new Date(item.completedAt).toLocaleString()}</>}
            {item.dueDate && <> · due {String(item.dueDate).slice(0, 10)}</>}
          </div>
          {lastComment && (
            <div style={{ fontSize: 13, color: T.text, marginTop: 6, fontStyle: "italic" }}>
              "{lastComment.comment}" — {lastComment.name}
            </div>
          )}
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <Tooltip title="Open the project's checklist (new tab) - item, thread and screenshots">
            <Button icon={<EyeOutlined />} href={`/checklists?projectId=${item.projectId?._id || item.projectId || ""}`} target="_blank" rel="noopener noreferrer">
              View checklist
            </Button>
          </Tooltip>
          <Button type="primary" style={navyBtn} icon={<CheckOutlined />} loading={working}
                  onClick={() => onApprove(item)}>
            Approve
          </Button>
          <Tooltip title="Send back to in progress — a reason is required">
            <Button danger icon={<CloseOutlined />} loading={working}
                    onClick={() => onRejectClick(item)}>
              Reject
            </Button>
          </Tooltip>
        </div>
      </div>
    </Card>
  );
}


// ======================= Imports tab ========================================
// Line items created by a 'review first' document import: approve (goes
// live on the schedule / issues / checklists) or reject (removed), per
// line or per file.
function ImportsTab({ projectId, filter, onCountChange, reloadKey }) {
  const dispatch = useDispatch();
  const settled = useSettledRows("imports");
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const loadedOnce = useRef(false);
  const load = useCallback(() => {
    if (!projectId) { setGroups([]); onCountChange && onCountChange(0); return; }
    if (!loadedOnce.current) setLoading(true);
    fetch(`${url}/projects/${projectId}/schedule-import/pending-items`, { headers: auth() })
      .then((r) => (r.ok ? r.json() : { imports: [], total: 0 }))
      .then((b) => { setGroups(b.imports || []); onCountChange && onCountChange(b.total || 0); })
      .catch(() => setGroups([]))
      .finally(() => { setLoading(false); loadedOnce.current = true; });
  }, [projectId, reloadKey]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);
  const decide = async (action, items) => {
    if (!items.length) return;
    setWorking(true);
    try {
      const r = await fetch(`${url}/projects/${projectId}/schedule-import/pending-items/decide`, { method: "POST", headers: { "Content-Type": "application/json", ...auth() }, body: JSON.stringify({ action, items: items.map((i) => ({ kind: i.kind, id: i.id })) }) });
      const b = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(b.err || `HTTP ${r.status}`);
      message.success(action === "approve" ? `${b.approved} item(s) are now live` : `${b.rejected} item(s) removed`);
      dispatch(rowsSettled("imports", items.map((i) => `${i.kind}:${i.id}`), action === "approve" ? "Approved" : "Removed"));
      load();
    } catch (e) { message.error(e.message); }
    finally { setWorking(false); }
  };
  const KIND = { schedule: { label: "Activity", color: "geekblue" }, issue: { label: "Issue", color: "volcano" }, checklist: { label: "Checklist", color: "cyan" } };
  const shownGroups = groups
    .map((g) => ({ ...g, items: settled.apply(g.items.filter((i) => matchesReviewFilter(i, filter)), (i) => `${i.kind}:${i.id}`) }))
    .filter((g) => g.items.length);
  // One selection across every import group on screen - a reviewer
  // clearing a morning's imports does not care which file each line came
  // from. Keyed kind+id because ids are only unique within a kind.
  const keyOf = (i) => `${i.kind}:${i.id}`;
  const allItems = shownGroups.flatMap((g) => g.items);
  const sel = useBulkSelection(allItems.filter((i) => !i._settled).map(keyOf));
  const byKey = new Map(allItems.map((i) => [keyOf(i), i]));
  if (!projectId) return <Empty description="Pick a project to review its imported items" />;
  return (
    <>
      {loading && <Skeleton active paragraph={{ rows: 3 }} />}
      {!loading && (
        <BulkBar
          sel={sel}
          busy={working}
          actions={[
            { key: "approve", label: "Approve selected", icon: <CheckOutlined />, primary: true,
              run: (ids) => decide("approve", ids.map((k) => byKey.get(k)).filter(Boolean)) },
            { key: "reject", label: "Reject selected", icon: <CloseOutlined />, danger: true,
              run: (ids) => decide("reject", ids.map((k) => byKey.get(k)).filter(Boolean)) },
          ]}
        />
      )}
      {!loading && groups.length === 0 && <Empty description="Nothing waiting - tick 'Review the imported line items before they go live' in the import wizard to route an import here." />}
      {!loading && shownGroups.map((g) => (
        <Card key={g.importId} size="small" style={{ marginBottom: 12 }}
          title={<span>{g.fileName || "Import"} <span style={{ color: T.muted, fontWeight: 400, fontSize: 12 }}>{g.importedAt ? new Date(g.importedAt).toLocaleString() : ""} · {g.items.length} item(s)</span></span>}
          extra={<Space>
            <Button size="small" type="primary" icon={<CheckOutlined />} loading={working} onClick={() => decide("approve", g.items)}>Approve all</Button>
            <Button size="small" danger icon={<CloseOutlined />} loading={working} onClick={() => decide("reject", g.items)}>Reject all</Button>
            {g.importId !== "other" && <a href={`/schedule-import/${projectId}?importId=${g.importId}`} target="_blank" rel="noreferrer">View import</a>}
          </Space>}>
          <Table size="small" rowKey={keyOf} pagination={{ pageSize: 25, hideOnSinglePage: true }} dataSource={g.items}
            rowSelection={{
              selectedRowKeys: sel.selectedIds.filter((k) => g.items.some((i) => keyOf(i) === k)),
              onChange: (keys) => {
                // Per-group table: replace only THIS group's contribution
                // so ticking here never clears another group's rows.
                sel.setMany(g.items.map(keyOf), false);
                sel.setMany(keys.map(String), true);
              },
            }}
            columns={[
              { title: "", dataIndex: "kind", width: 90, render: (k) => <Tag color={KIND[k]?.color}>{KIND[k]?.label || k}</Tag> },
              { title: "Line item", dataIndex: "title", render: (t, r) => <span>{t}<div style={{ fontSize: 11, color: T.muted }}>{r.sub}{r.contractorLabel ? ` · ${r.contractorLabel}` : ""}{r.table ? ` · ${r.table}${r.row ? ` row ${r.row}` : ""}` : ""}</div></span> },
              { title: "", key: "a", width: 170, render: (_, r) => <Space size={4}>
                <Button size="small" type="primary" onClick={() => decide("approve", [r])}>Approve</Button>
                <Button size="small" danger onClick={() => decide("reject", [r])}>Reject</Button>
              </Space> },
            ]} />
        </Card>
      ))}
    </>
  );
}

// ======================= Schedule updates tab ===============================
// What partners have said about the schedule, waiting on the owner. Two
// kinds, reviewed side by side because they are the same decision from a
// reviewer's seat - a contractor telling you something about the plan
// that does not touch it until you say yes:
//   progress  "we are x% done on our activity"
//   claim     "this unassigned activity is ours" - approving hands it
//             over AND files it into the area breakdown the claimant
//             named (back/routes/ganttItems POST /:id/claim).
function ScheduleUpdatesTab({ projectId, filter, onCountChange, reloadKey }) {
  const dispatch = useDispatch();
  const settled = useSettledRows("schedule");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState("");
  const [noteFor, setNoteFor] = useState(null); // {row, action}
  const [note, setNote] = useState("");
  const loadedOnce = useRef(false);
  const load = useCallback(() => {
    if (!projectId) { setRows([]); onCountChange && onCountChange(0); return; }
    if (!loadedOnce.current) setLoading(true);
    const get = (path) => fetch(`${url}/projects/${projectId}/gantt-items/${path}`, { headers: auth() })
      .then((r) => (r.ok ? r.json() : []))
      .then((l) => (Array.isArray(l) ? l : []))
      .catch(() => []);
    Promise.all([get("pending-progress"), get("pending-claims")])
      .then(([progress, claims]) => {
        const arr = [
          ...claims.map((r) => ({ ...r, _kind: "claim", _at: r.pendingClaim?.at })),
          ...progress.map((r) => ({ ...r, _kind: "progress", _at: r.pendingProgress?.at })),
        ].sort((a, b) => new Date(b._at || 0) - new Date(a._at || 0));
        setRows(arr);
        onCountChange && onCountChange(arr.length);
      })
      .finally(() => { setLoading(false); loadedOnce.current = true; });
  }, [projectId, reloadKey]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);
  const act = async (row, action, txt) => {
    setWorking(row._id);
    try {
      const path = row._kind === "claim" ? "claim" : "pending-progress";
      const r = await fetch(`${url}/projects/${projectId}/gantt-items/${row._id}/${path}/${action}`, { method: "POST", headers: { "Content-Type": "application/json", ...auth() }, body: JSON.stringify({ note: txt || "" }) });
      const b = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(b.err || `HTTP ${r.status}`);
      message.success(
        row._kind === "claim"
          ? (action === "approve"
              ? `"${row.name}" is now ${row.pendingClaim?.companyName || "theirs"}${row.pendingClaim?.areaKey ? ` in ${row.pendingClaim.areaKey}` : ""}`
              : "Claim rejected")
          : (action === "approve" ? `Applied ${row.pendingProgress?.progress}% to "${row.name}"` : "Report rejected"));
      dispatch(rowSettled("schedule", row._id, action === "approve" ? "Applied" : "Rejected"));
      setNoteFor(null); setNote("");
      load();
    } catch (e) { message.error(e.message); }
    finally { setWorking(""); }
  };
  const shown = settled.apply(rows.filter((r) => matchesReviewFilter(r, filter)));
  const sel = useBulkSelection(shown.filter((r) => !r._settled).map((r) => String(r._id)));
  const byId = new Map(shown.map((r) => [String(r._id), r]));
  const bulk = async (ids, action, txt) => {
    await runBatch(ids, async (id) => {
      const row = byId.get(id);
      if (!row) return;
      // Same split as act(): the two kinds share this list but not
      // their endpoint.
      const path = row._kind === "claim" ? "claim" : "pending-progress";
      const r = await fetch(`${url}/projects/${projectId}/gantt-items/${id}/${path}/${action}`, {
        method: "POST", headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify({ note: txt || "" }),
      });
      if (!r.ok) throw (await r.json().catch(() => ({ err: `HTTP ${r.status}` })));
    }, { verb: action === "approve" ? "applied to the schedule" : "rejected" }).then(({ failed }) => {
      const bad = new Set(failed.map((f) => String(f.id)));
      dispatch(rowsSettled("schedule", ids.filter((id) => !bad.has(String(id))), action === "approve" ? "Applied" : "Rejected"));
    });
    load();
  };
  if (!projectId) return <Empty description="Pick a project to see what is waiting on the schedule" />;
  return (
    <>
      {loading && <Skeleton active paragraph={{ rows: 3 }} />}
      {!loading && (
        <BulkBar
          sel={sel}
          actions={[
            { key: "approve", label: "Approve selected", icon: <CheckOutlined />, primary: true, run: (ids) => bulk(ids, "approve") },
            { key: "reject", label: "Reject selected", icon: <CloseOutlined />, danger: true, run: (ids) => bulk(ids, "reject", note) },
          ]}
          hint="rejections use the note from the last reject dialog, if any"
        />
      )}
      {!loading && rows.length === 0 && <Empty description="Nothing waiting - partners' progress reports, and their claims on unassigned activities, land here before the schedule changes." />}
      {!loading && rows.length > 0 && shown.length === 0 && <Empty description="No reports match the filter" />}
      {!loading && shown.map((r) => (
        <Card key={r._id} size="small" style={{ marginBottom: 10, ...settledStyle(r) }}
          title={<span><RowCheck sel={sel} id={String(r._id)} />{r.wbs ? `${r.wbs} ` : ""}{r.name} <Tag>{r.type}</Tag>{r._kind === "claim" ? <Tag color="orange">claim</Tag> : null}<SettledBadge row={r} /></span>}
          extra={<a href={`/scheduler/${projectId}?focus=${r._id}`} target="_blank" rel="noreferrer">Open in schedule</a>}>
          <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
            {r._kind === "claim" ? (
            <div style={{ fontSize: 13 }}>
              <b>{r.pendingClaim?.companyName || r.pendingClaim?.name || "A partner"}</b> asked to take this activity on
              <span style={{ color: T.muted }}> · {r.pendingClaim?.at ? new Date(r.pendingClaim.at).toLocaleString() : ""}</span>
              <div style={{ marginTop: 4 }}>
                Area: <b style={{ color: T.ink }}>{r.pendingClaim?.areaKey || "not given"}</b>
                {r.pendingClaim?.name ? <span style={{ color: T.muted }}> · asked by {r.pendingClaim.name}</span> : null}
              </div>
              {r.pendingClaim?.note && <div style={{ color: "#444", marginTop: 4 }}>"{r.pendingClaim.note}"</div>}
              <div style={{ color: T.muted, fontSize: 12, marginTop: 2 }}>{r.startDate ? new Date(r.startDate).toLocaleDateString() : ""} - {r.endDate ? new Date(r.endDate).toLocaleDateString() : ""}</div>
            </div>
            ) : (
            <div style={{ fontSize: 13 }}>
              <b>{r.pendingProgress?.name || "Partner"}</b> reported <b style={{ color: T.ink }}>{r.pendingProgress?.progress}%</b>
              <span style={{ color: T.muted }}> (currently {r.progress || 0}%) · {r.pendingProgress?.at ? new Date(r.pendingProgress.at).toLocaleString() : ""}</span>
              {r.pendingProgress?.note && <div style={{ color: "#444", marginTop: 4 }}>"{r.pendingProgress.note}"</div>}
              <div style={{ color: T.muted, fontSize: 12, marginTop: 2 }}>{r.startDate ? new Date(r.startDate).toLocaleDateString() : ""} - {r.endDate ? new Date(r.endDate).toLocaleDateString() : ""}{r.responsibleUserName ? ` · responsible ${r.responsibleUserName}` : ""}</div>
            </div>
            )}
            <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
              <Button size="small" type="primary" icon={<CheckOutlined />} loading={working === r._id} onClick={() => act(r, "approve")}>{r._kind === "claim" ? "Approve the claim" : `Approve${r.pendingProgress?.progress >= 100 ? " (mark done)" : ""}`}</Button>
              <Button size="small" danger icon={<CloseOutlined />} onClick={() => { setNoteFor({ row: r, action: "reject" }); setNote(""); }}>Reject</Button>
            </div>
          </div>
        </Card>
      ))}
      <Modal open={!!noteFor} title={noteFor ? `Reject the report on "${noteFor.row.name}"` : ""} onCancel={() => setNoteFor(null)}
        onOk={() => noteFor && act(noteFor.row, noteFor.action, note)} okText="Reject" okButtonProps={{ danger: true }} confirmLoading={!!working}>
        <Input.TextArea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Tell the reporter why (optional)" />
      </Modal>
    </>
  );
}

// ======================= Checklist completions ==============================
function ChecklistCompletionsTab({ items: rawItems, loading, working, onApprove, onReject, filter }) {
  const settled = useSettledRows("checklist");
  const items = settled.apply(rawItems || []);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectNote, setRejectNote] = useState("");
  const [rejectError, setRejectError] = useState("");

  const openReject = (item) => {
    setRejectTarget(item);
    setRejectNote("");
    setRejectError("");
  };
  const submitReject = async () => {
    if (!rejectNote.trim()) {
      setRejectError("A short reason is required — the assignee sees it.");
      return;
    }
    // rejectTarget is a single item, or an array when a batch is being
    // rejected from the bulk bar - the assignee's reason is the same
    // either way, which is why the modal is shared.
    const targets = Array.isArray(rejectTarget) ? rejectTarget : [rejectTarget];
    let allOk = true;
    for (const t of targets) {
      const ok = await onReject(t, rejectNote.trim());
      if (!ok) allOk = false;
    }
    if (allOk) setRejectTarget(null);
  };

  const visible = (items || []).filter((i) => matchesReviewFilter(i, filter));
  const sel = useBulkSelection(visible.map((i) => String(i._id)));
  const byId = new Map(visible.map((i) => [String(i._id), i]));

  return (
    <>
      {loading && <Skeleton active paragraph={{ rows: 4 }} />}
      {!loading && (
        <BulkBar
          sel={sel}
          busy={working}
          actions={[
            {
              key: "approve", label: "Approve selected", icon: <CheckOutlined />, primary: true,
              run: (ids) => runBatch(ids, (id) => onApprove(byId.get(id)), { verb: "approved" }),
            },
            {
              key: "reject", label: "Reject selected…", icon: <CloseOutlined />, danger: true,
              // Rejection needs a reason the assignee will read, so the
              // batch goes through the same modal a single one does.
              run: async (ids) => {
                setRejectNote("");
                setRejectError("");
                setRejectTarget(ids.map((id) => byId.get(id)).filter(Boolean));
              },
            },
          ]}
        />
      )}

      {!loading && items.length === 0 && (
        <Empty description="No checklist completions waiting for approval" />
      )}

      {!loading && visible.map((item) => (
        <ChecklistCompletionCard key={item._id} item={item}
                                 onApprove={onApprove} onRejectClick={openReject}
                                 working={working} sel={sel} />
      ))}

      <Modal
        title={
          Array.isArray(rejectTarget)
            ? `Reject ${rejectTarget.length} completion${rejectTarget.length === 1 ? "" : "s"}`
            : (rejectTarget ? `Reject "${rejectTarget.title}"` : "")
        }
        open={!!rejectTarget}
        onCancel={() => setRejectTarget(null)}
        onOk={submitReject}
        okText="Reject completion"
        okButtonProps={{ danger: true }}
        confirmLoading={working}
        destroyOnClose
      >
        <div style={{ fontSize: 13, color: T.text, marginBottom: 10 }}>
          The item goes back to <b>in progress</b> and the validate link
          reopens for the assignee. Tell them what's missing:
        </div>
        <Input.TextArea
          rows={3}
          placeholder="Reason (required)"
          value={rejectNote}
          onChange={(e) => { setRejectNote(e.target.value); setRejectError(""); }}
          status={rejectError ? "error" : ""}
        />
        {rejectError && (
          <div style={{ color: "#ff4d4f", fontSize: 12, marginTop: 4 }}>{rejectError}</div>
        )}
      </Modal>
    </>
  );
}

// =============================== Page ======================================

export default function ApprovalQueue() {
  const history = useHistory();
  const dispatch = useDispatch();
  // ONE refresh for seven queues. Each tab loads its own rows, so the icon
  // bumps a key every tab has in the dependency list of its load(): all of
  // them refetch, the page is not reloaded, and the project, the search
  // box, the day filter and the open tab all survive.
  //
  // The key is the shared issue-list tick, so a refresh here also brings
  // the issue manager up to date, and vice versa (reducers/issues.js).
  const { refreshTick } = useIssueListState();
  const [refreshing, setRefreshing] = useState(false);
  const refreshAll = () => {
    setRefreshing(true);
    dispatch(refreshIssueLists());
    loadBadges();
    // The spin is the acknowledgement; the tabs answer on their own clock.
    setTimeout(() => setRefreshing(false), 900);
  };
  const [projects, setProjects] = useState([]);
  // Remembers the last project (shared with the other coordination pages);
  // ?projectId= in the URL wins for this visit.
  const [projectId, setProjectId] = useLastProject(projects);

  useEffect(() => {
    let linked = "";
    try { linked = new URLSearchParams(window.location.search).get("projectId") || ""; } catch (e) { linked = ""; }
    fetchProjects(linked).then(setProjects);
  }, []);

  // Checklist completion queue — held here (not in the tab) so the tab
  // label can badge the pending count without the tab being opened.
  // Per-tab counts for the badges: { [tab]: { pending, unseen, ids } }.
  const [badges, setBadges] = useState({});
  // ?tab= opens a tab directly (the Technical documentation page links to
  // its Documentation tab); otherwise the tab this person last used
  // (helpers/userPrefs, owner 2026-09-18: remembered "by user").
  const [urlTab, setUrlTab] = useState(() => {
    try { return new URLSearchParams(window.location.search).get("tab") || ""; } catch (e) { return ""; }
  });
  const [savedTab, setSavedTab] = usePreference("reviewCenter.tab", "proposals");
  const activeTab = urlTab || savedTab || "proposals";
  const setActiveTab = (t) => { setUrlTab(""); setSavedTab(t); };
  const loadBadges = useCallback(() => {
    if (!projectId) { setBadges({}); return Promise.resolve(); }
    return fetchBadges(projectId).then((r) => setBadges(r.tabs || {})).catch(() => {});
  }, [projectId]);
  useEffect(() => { loadBadges(); }, [loadBadges, refreshTick]);
  // Kept fresh while the page is open: a partner's submission arriving
  // while the owner reads another tab should show up on its badge.
  useEffect(() => {
    if (!projectId) return undefined;
    const t = setInterval(loadBadges, 60000);
    return () => clearInterval(t);
  }, [projectId, loadBadges]);
  // Looking at a tab is what clears its red number: the items on it are
  // reported as seen, and the badge falls to what arrived since.
  const markSeen = useCallback((tab) => {
    const b = badges[tab];
    if (!projectId || !b || !b.unseen) return;
    markTabSeen(projectId, tab, b.ids || []).then(() => loadBadges()).catch(() => {});
  }, [projectId, badges, loadBadges]);
  useEffect(() => { markSeen(activeTab); }, [activeTab, badges]); // eslint-disable-line react-hooks/exhaustive-deps
  // The red circle every notification badge is (owner, 2026-09-14:
  // "red like typical notif circle icons"). Both states are red so a
  // pending count never reads as "nothing to do": what is new since you
  // last looked is the solid circle; what is pending but already seen is
  // the same red as an outline, so the distinction survives without a
  // second colour.
  const NOTIF_RED = "#ff4d4f";
  const tabBadge = (tab, fallback) => {
    const b = badges[tab];
    const unseen = b ? b.unseen : 0;
    const pending = b ? b.pending : (fallback || 0);
    if (unseen > 0) {
      return (
        <Tooltip title={`${unseen} new since you last looked · ${pending} to review`}>
          <Badge count={unseen} size="small" offset={[6, -2]} style={{ backgroundColor: NOTIF_RED, color: "#fff" }} />
        </Tooltip>
      );
    }
    if (pending > 0) {
      return (
        <Tooltip title={`${pending} to review, all seen`}>
          <Badge
            count={pending} size="small" offset={[6, -2]}
            style={{ backgroundColor: "#fff", color: NOTIF_RED, boxShadow: `0 0 0 1px ${NOTIF_RED} inset` }}
          />
        </Tooltip>
      );
    }
    return null;
  };
  const [checklistQueue, setChecklistQueue] = useState([]);
  const [scheduleUpdatesCount, setScheduleUpdatesCount] = useState(0);
  const [importsCount, setImportsCount] = useState(0);
  const [minutesCount, setMinutesCount] = useState(0);
  const [markupsCount, setMarkupsCount] = useState(0);
  // Its own count as well as the badge's: the badge comes from one call for
  // every tab and the tab knows its own rows, and a tab whose label waits
  // on the shared call reads "0" for a second on every load.
  const [shopDrawingsCount, setShopDrawingsCount] = useState(0);
  // Partner documentation imports waiting for review (owner, 2026-10-15). The
  // tab is the project team's - participantView AND reviewCenterView, which
  // the server folds into can.partnerImportView - so partners never see it.
  const [partnerImportsCount, setPartnerImportsCount] = useState(0);
  const { can: projectCan, isOwnerCompany: projectIsOwnerSide } = useProjectAccess(projectId);
  // Model pushes waiting to be merged (Project.requireCommitApproval).
  const [revisionsCount, setRevisionsCount] = useState(0);
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [checklistWorking, setChecklistWorking] = useState(false);

  const checklistLoadedOnce = useRef(false);
  const loadChecklistQueue = useCallback(() => {
    if (!checklistLoadedOnce.current) setChecklistLoading(true);
    fetchChecklistQueue(projectId)
      .then((rows) => setChecklistQueue(Array.isArray(rows) ? rows : []))
      .catch((e) => {
        if (e.status === 403) {
          message.error("Only project owners and admins can review checklist completions");
        } else {
          message.error(e.message || "Failed to load checklist completions");
        }
      })
      .finally(() => { setChecklistLoading(false); checklistLoadedOnce.current = true; });
  }, [projectId, refreshTick]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadChecklistQueue(); }, [loadChecklistQueue]);

  const handleChecklistApprove = async (item) => {
    setChecklistWorking(true);
    try {
      await reviewChecklist(item._id, "approve", "");
      dispatch(rowSettled("checklist", item._id, "Approved"));
      message.success(`"${item.title}" approved — marked complete`);
    } catch (e) {
      if (e.status === 403) {
        message.error("Only project owners and admins can review checklist completions");
      } else {
        message.error(e.message || "Approve failed");
      }
    } finally {
      setChecklistWorking(false);
    }
  };
  const handleChecklistReject = async (item, note) => {
    setChecklistWorking(true);
    try {
      await reviewChecklist(item._id, "reject", note);
      dispatch(rowSettled("checklist", item._id, "Sent back"));
      message.success(`"${item.title}" sent back to in progress`);
      return true;
    } catch (e) {
      if (e.status === 403) {
        message.error("Only project owners and admins can review checklist completions");
      } else {
        message.error(e.message || "Reject failed");
      }
      return false;
    } finally {
      setChecklistWorking(false);
    }
  };

  // Keep ?projectId= in the URL so links from the coordination
  // dashboard land pre-filtered.
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    if (projectId) sp.set("projectId", projectId);
    else sp.delete("projectId");
    history.replace(`${window.location.pathname}${sp.toString() ? `?${sp.toString()}` : ""}`);
  }, [projectId, history]);

  // Search / day filter shared by every tab (client-side): free text over
  // names, partner / contractor, responsible person, status; a day over
  // the rows' dates; "Today" = that day set to today.
  const [q, setQ] = useState("");
  const [day, setDay] = useState("");
  // Windows rather than a single day: "everything submitted while we were
  // closing out August", "due before Friday's meeting". Each tab's rows
  // carry a different field name for the same two questions, so the
  // matcher resolves them by priority — see helpers/reviewFilter.js.
  const [startRange, setStartRange] = useState({ from: "", to: "" });
  const [dueRange, setDueRange] = useState({ from: "", to: "" });
  const filter = useMemo(
    () => ({ q, date: day, startRange, dueRange }),
    [q, day, startRange, dueRange]
  );
  const isToday = day === todayIso();
  const anyFilter = q || day || !rangeEmpty(startRange) || !rangeEmpty(dueRange);

  const tabItems = [
    {
      key: "proposals",
      label: <span title="Resolutions to approve">Resolutions{tabBadge("proposals")}</span>,
      children: <ProposalsTab projectId={projectId} filter={filter} reloadKey={refreshTick} />,
    },
    {
      key: "checklist",
      label: (
        <span title="Checklist completions to sign off">
          Checklists
          {tabBadge("checklist", checklistQueue.length)}
        </span>
      ),
      children: (
        <ChecklistCompletionsTab
          items={checklistQueue}
          loading={checklistLoading}
          working={checklistWorking}
          onApprove={handleChecklistApprove}
          onReject={handleChecklistReject}
          filter={filter}
        />
      ),
    },
    {
      key: "schedule",
      label: (
        <span title="Schedule updates">
          Schedule
          {tabBadge("schedule", scheduleUpdatesCount)}
        </span>
      ),
      children: <ScheduleUpdatesTab projectId={projectId} filter={filter} onCountChange={setScheduleUpdatesCount} reloadKey={refreshTick} />,
    },
    {
      key: "markups",
      label: (
        <span title="Traced as-built markups: element statuses to approve">
          Markups
          {tabBadge("markups", markupsCount)}
        </span>
      ),
      children: <MarkupsTab projectId={projectId} reloadKey={refreshTick} onCountChange={setMarkupsCount} />,
    },
    {
      key: "imports",
      label: (
        <span>
          Imports
          {tabBadge("imports", importsCount)}
        </span>
      ),
      children: <ImportsTab projectId={projectId} filter={filter} onCountChange={setImportsCount} reloadKey={refreshTick} />,
    },
    {
      key: "minutes",
      label: (
        <span title="Minutes to apply">
          Minutes
          {tabBadge("minutes", minutesCount)}
        </span>
      ),
      children: <MinutesTab projectId={projectId} onCountChange={setMinutesCount} filter={filter} reloadKey={refreshTick} />,
    },
    {
      key: "revisions",
      label: (
        <span>
          Revisions
          {tabBadge("revisions", revisionsCount)}
        </span>
      ),
      children: <RevisionsTab projectId={projectId} filter={filter} onCountChange={setRevisionsCount} reloadKey={refreshTick} />,
    },
    {
      key: "triage",
      label: <span title="New partner issues to triage">Triage{tabBadge("triage")}</span>,
      children: <TriageTab projectId={projectId} filter={filter} reloadKey={refreshTick} />,
    },
    ...(projectIsOwnerSide ? [{
      // Owner side only: approving is a reviewer's act (the server refuses
      // anybody else), so a partner is not shown a queue it cannot clear.
      key: "approval",
      label: <span title="Open issues not yet approved to resolve">Approve to resolve{tabBadge("approval")}</span>,
      children: <ApprovalTab projectId={projectId} filter={filter} reloadKey={refreshTick} onDecided={loadBadges} />,
    }] : []),
    {
      // SHOP DRAWINGS (owner, 2026-10-05: "let the approval also show in the
      // review center, not only in the shop drawings"). The badge for this
      // tab has existed since the lane was built - routes/reviewCenter has
      // counted drawings at `under_review` all along - so the count was
      // computed, reported and had nowhere to go.
      key: "shopDrawings",
      label: (
        <span title="Delivered shop drawings waiting for approval - approving signs off the schedule activity">
          Shop drawings
          {tabBadge("shopDrawings", shopDrawingsCount)}
        </span>
      ),
      children: (
        <ShopDrawingsTab
          projectId={projectId}
          filter={filter}
          reloadKey={refreshTick}
          onCountChange={setShopDrawingsCount}
          onDecided={loadBadges}
        />
      ),
    },
    ...(projectCan.partnerImportView ? [{
      // PARTNER IMPORT (owner, 2026-10-15): partners read out of the documents
      // imported on a project's Partners page - accept, edit, map or reject.
      key: "partnerImports",
      label: (
        <span title="Partners read from imported partner documentation, waiting for review">
          Partner import
          {tabBadge("partnerImports", partnerImportsCount)}
        </span>
      ),
      children: (
        <PartnerImportTab
          projectId={projectId}
          reloadKey={refreshTick}
          onCountChange={setPartnerImportsCount}
          onDecided={loadBadges}
        />
      ),
    }] : []),
    {
      key: "documentation",
      label: <span>Documentation{tabBadge("documentation")}</span>,
      children: <DocumentationReview projectId={projectId} filter={q} reloadKey={refreshTick} onDecided={loadBadges} />,
    },
  ];

  return (
    // Centred on the page (owner, 2026-09-18), with every tab in view: the
    // labels are the short names, the full ones are their hover titles.
    <PageContainer style={{ fontFamily: "Inter, system-ui, sans-serif" }}>
      <CoordinationBreadcrumbs current="Review Center" />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
        <div>
          <h2 style={{ margin: 0, color: T.ink }}>Review Center</h2>
          <div style={{ color: T.muted, fontSize: 13, marginTop: 4 }}>
            Proposals to approve, checklist completions to sign off,
            partner issues to triage, and resolved issues to confirm
            and close.
          </div>
        </div>
        <Space>
          <Tooltip title="Reload every queue without reloading the page - keeps the project, the search and the tab you are on">
            <Button icon={<ReloadOutlined spin={refreshing} />} onClick={refreshAll} />
          </Tooltip>
          <Select style={{ width: 280 }} value={projectId || undefined}
                  onChange={(v) => setProjectId(v || "")}
                  allowClear showSearch optionFilterProp="label"
                  filterOption={(input, option) => String(option?.label || "").toLowerCase().includes(input.toLowerCase())}
                  placeholder="All projects (type to search)"
                  options={projects.map(p => ({ label: p.title, value: p._id }))} />
        </Space>
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
        <Input.Search allowClear placeholder="Search by name, partner / contractor, responsible person, status…" value={q}
                      onChange={(e) => setQ(e.target.value)} style={{ width: 420, maxWidth: "100%" }} />
        <input type="date" value={day} onChange={(e) => setDay(e.target.value)}
               style={{ border: "1px solid #d9d9d9", borderRadius: 6, padding: "4px 8px", fontSize: 13 }} title="Only rows dated on this day (due / submitted / updated)" />
        <Button type={isToday ? "primary" : "default"} onClick={() => setDay(isToday ? "" : todayIso())}>Today</Button>
        <DateRangeFilter
          label="Created / start"
          value={startRange}
          onChange={setStartRange}
          title="Rows submitted, raised or starting between these two days, inclusive. Leave either end empty for an open window."
        />
        <DateRangeFilter
          label="Due / end"
          value={dueRange}
          onChange={setDueRange}
          title="Rows due or ending between these two days, inclusive. Rows with no such date are left out."
        />
        {anyFilter && (
          <Button type="text" onClick={() => {
            setQ(""); setDay("");
            setStartRange({ from: "", to: "" }); setDueRange({ from: "", to: "" });
          }}>Clear</Button>
        )}
      </div>
      <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} tabBarGutter={20} />
    </PageContainer>
  );
}
